import { resolveScheduleTimeZone, getCountryDisplayName } from './visitorLocale.js';

/** Zona horaria de referencia del equipo (agendas desde 13:30). */
export const DEMO_ANCHOR_TIMEZONE = 'America/Montevideo';
export const DEMO_ANCHOR_GMT_OFFSET = 'GMT-3';
export const DEMO_ANCHOR_START = '13:30';

export function getViewerTimeZone(): string {
  return resolveScheduleTimeZone();
}

export function getViewerTimezoneLabel(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('es', {
      timeZone,
      timeZoneName: 'long',
    }).formatToParts(new Date());
    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (tzName) return tzName;
  } catch {
    /* ignore */
  }
  return timeZone.replace(/_/g, ' ');
}

export function formatTimeInZone(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('es', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

export function formatDayInZone(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('es', {
    timeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(iso));
}

export function formatMeetingInZone(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('es', {
    timeZone,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

export function dayKeyInZone(iso: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(iso));
  const y = parts.find((p) => p.type === 'year')?.value ?? '0000';
  const m = parts.find((p) => p.type === 'month')?.value ?? '01';
  const d = parts.find((p) => p.type === 'day')?.value ?? '01';
  return `${y}-${m}-${d}`;
}

/** Hora local equivalente al inicio diario (13:30 Uruguay). */
export function anchorStartInViewerZone(viewerTz: string, refDate = new Date()): string {
  const ymd = new Intl.DateTimeFormat('en-CA', {
    timeZone: DEMO_ANCHOR_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(refDate);
  const [y, m, d] = ymd.split('-').map(Number);
  const anchorUtc = new Date(Date.UTC(y, m - 1, d, 16, 30, 0));
  return formatTimeInZone(anchorUtc.toISOString(), viewerTz);
}

export function viewerSharesAnchorClock(viewerTz: string, iso: string): boolean {
  return formatTimeInZone(iso, viewerTz) === formatTimeInZone(iso, DEMO_ANCHOR_TIMEZONE);
}

export function formatSlotLabelForViewer(
  iso: string,
  viewerTz: string
): { primary: string; secondary: string } {
  const uyTime = formatTimeInZone(iso, DEMO_ANCHOR_TIMEZONE);
  const uyRef = `${uyTime} Uruguay (${DEMO_ANCHOR_GMT_OFFSET})`;

  if (viewerTz === DEMO_ANCHOR_TIMEZONE || viewerSharesAnchorClock(viewerTz, iso)) {
    return { primary: uyTime, secondary: `Uruguay (${DEMO_ANCHOR_GMT_OFFSET})` };
  }

  return {
    primary: formatTimeInZone(iso, viewerTz),
    secondary: uyRef,
  };
}

export function buildScheduleAnchorNotice(viewerTz: string, countryIso?: string): string {
  const countryName = countryIso ? getCountryDisplayName(countryIso) : undefined;
  const localized =
    countryName && countryIso !== 'UY'
      ? `Horarios mostrados para ${countryName} según tu ubicación o idioma del navegador. `
      : '';

  const base = `Las agendas empiezan a las ${DEMO_ANCHOR_START} hora Uruguay (${DEMO_ANCHOR_GMT_OFFSET}).`;

  if (viewerTz === DEMO_ANCHOR_TIMEZONE) {
    return `${localized}${base} Los horarios se muestran en hora local de Uruguay.`;
  }

  const localStart = anchorStartInViewerZone(viewerTz);
  const tzLabel = getViewerTimezoneLabel(viewerTz);

  return `${localized}${base} En ${tzLabel} el primer turno del día es aprox. las ${localStart}; abajo ves tu hora local y, debajo, la de Uruguay.`;
}

export function formatMeetingConfirmation(
  iso: string,
  viewerTz: string
): { when: string; timezoneNote: string } {
  const uyWhen = formatMeetingInZone(iso, DEMO_ANCHOR_TIMEZONE);

  if (viewerTz === DEMO_ANCHOR_TIMEZONE) {
    return {
      when: uyWhen,
      timezoneNote: `Hora Uruguay (${DEMO_ANCHOR_GMT_OFFSET})`,
    };
  }

  const localWhen = formatMeetingInZone(iso, viewerTz);
  if (viewerSharesAnchorClock(viewerTz, iso)) {
    return {
      when: uyWhen,
      timezoneNote: `Hora Uruguay (${DEMO_ANCHOR_GMT_OFFSET})`,
    };
  }

  return {
    when: localWhen,
    timezoneNote: `${uyWhen} · hora Uruguay (${DEMO_ANCHOR_GMT_OFFSET})`,
  };
}
